import React, { useEffect, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { closePatientDrawer } from "portal/slices/patientDrawerSlice";
import patientService from "portal/services/patientService";
import { getAvatarColors, formatTime } from "portal/utils/formatters";
import { useNavigate } from "react-router-dom";
import allRoutes from "client/constants/allRoutes";
import { useBranding } from "context/BrandingProvider";
import { GoogleMeetIcon } from "portal/components/GoogleMeetBadge";

const TABS = ["Overview", "Sessions", "Payments"];

const PatientDetailSlidePanel = () => {
  const { currency } = useBranding();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isOpen, patientId } = useSelector((state) => state.patientDrawer);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("Overview");
  const [sessions, setSessions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  const handleClose = useCallback(() => {
    dispatch(closePatientDrawer());
  }, [dispatch]);

  // ESC key closes
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") handleClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleClose]);

  // Reset tab when opening
  useEffect(() => {
    if (isOpen) setActiveTab("Overview");
  }, [isOpen, patientId]);

  // Fetch patient details when opened
  useEffect(() => {
    if (isOpen && patientId) {
      const fetchDetails = async () => {
        setLoading(true);
        try {
          const res = await patientService.getDetails({ userId: patientId });
          if (res?.rescode === 1) {
            setPatient(res.data);
          }
        } catch (e) {
          // error handled silently
        } finally {
          setLoading(false);
        }
      };
      fetchDetails();
    } else {
      setPatient(null);
      setSessions([]);
      setPayments([]);
    }
  }, [isOpen, patientId]);

  // Fetch sessions when Sessions tab is active
  useEffect(() => {
    if (activeTab === "Sessions" && patientId && sessions.length === 0) {
      const fetchSessions = async () => {
        setSessionsLoading(true);
        try {
          // /admin/patient-session takes perPage (its strict schema refused `limit`, so this tab
          // never loaded) and sorts oldest first: this month's sessions, as the profile opens on.
          const now = new Date();
          const res = await patientService.getSessions({ userId: patientId, page: 1, perPage: 10, month: now.getMonth() + 1, year: now.getFullYear() });
          if (res?.rescode === 1) {
            setSessions(res.data?.appointments || []);
          }
        } catch (e) { /* silent */ }
        finally { setSessionsLoading(false); }
      };
      fetchSessions();
    }
  }, [activeTab, patientId, sessions.length]);

  // Fetch payments when Payments tab is active
  useEffect(() => {
    if (activeTab === "Payments" && patientId && payments.length === 0) {
      const fetchPayments = async () => {
        setPaymentsLoading(true);
        try {
          const res = await patientService.getPaymentHistory({ userId: patientId, page: 1, limit: 10 });
          if (res?.rescode === 1) {
            setPayments(res.data?.appointment_payment_history || []);
          }
        } catch (e) { /* silent */ }
        finally { setPaymentsLoading(false); }
      };
      fetchPayments();
    }
  }, [activeTab, patientId, payments.length]);

  if (!isOpen) return null;

  const calculateAge = (dobStr) => {
    if (!dobStr) return "—";
    const birthDate = new Date(dobStr);
    if (isNaN(birthDate.getTime())) return "—";
    const today = new Date();
    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    if (months < 0) { years--; months += 12; }
    const parts = [];
    if (years > 0) parts.push(`${years}y`);
    if (months > 0) parts.push(`${months}m`);
    return parts.length ? parts.join(" ") : "0m";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const colors = patient ? getAvatarColors(`${patient.FirstName || ""} ${patient.LastName || ""}`) : { bgColor: "#E0F3FF", textColor: "#1A73E8" };
  const initials = patient ? `${(patient.FirstName || "")[0] || ""}${(patient.LastName || "")[0] || ""}`.toUpperCase() : "";

  const infoRows = patient
    ? [
        { label: "Father", value: patient.fatherName },
        { label: "Mother", value: patient.motherName },
        { label: "Gender", value: patient.gender },
        { label: "Age", value: calculateAge(patient.dob) },
        { label: "DOB", value: patient.dob ? formatDate(patient.dob) : null },
        { label: "Phone", value: patient.Phone },
        { label: "Email", value: patient.Email },
        { label: "Address", value: patient.address },
      ]
    : [];

  // Quick stats from patient data
  const quickStats = patient ? [
    { label: "Age", value: calculateAge(patient.dob), icon: "👤" },
    { label: "Since", value: patient.joining_date ? formatDate(patient.joining_date) : "—", icon: "📅" },
    // BR-PAT-2: an Evaluation patient is active in the system but not a regular patient yet.
    { label: "Status", value: !patient.IsActive ? "Inactive" : Number(patient.is_evaluation) === 1 ? "Evaluation" : "Active", color: !patient.IsActive ? "text-red-500 bg-red-50" : Number(patient.is_evaluation) === 1 ? "text-brand-600 bg-brand-50" : "text-green-600 bg-green-50", icon: "●" },
  ] : [];

  const SkeletonRows = ({ count = 4 }) => (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex justify-between">
          <div className="h-3 bg-surface-hover rounded w-1/4 animate-pulse" />
          <div className="h-3 bg-surface-hover rounded w-1/3 animate-pulse" />
        </div>
      ))}
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[998] animate-fadeIn"
          style={{ backgroundColor: "rgba(0,0,0,0.3)", backdropFilter: "blur(2px)" }}
          onClick={handleClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full z-[999] bg-surface-card shadow-2xl transition-transform duration-300 ease-in-out flex flex-col w-full sm:w-[440px] ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-surface-border flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-content-primary">Patient Preview</h3>
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-button hover:bg-surface-hover transition-colors flex-shrink-0"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1L13 13M1 13L13 1" stroke="#999" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="px-5 py-5 space-y-4 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-surface-hover" />
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-surface-hover rounded w-2/3" />
                  <div className="h-3 bg-surface-hover rounded w-1/2" />
                </div>
              </div>
              <div className="space-y-3 mt-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="flex justify-between">
                    <div className="h-3 bg-surface-hover rounded w-1/4" />
                    <div className="h-3 bg-surface-hover rounded w-1/3" />
                  </div>
                ))}
              </div>
            </div>
          ) : patient ? (
            <div>
              {/* Avatar + Name + Quick Stats */}
              <div className="px-5 pt-5 pb-3">
                <div className="flex items-center gap-4 mb-4">
                  <span
                    className="rounded-full w-14 h-14 flex justify-center items-center font-bold text-lg shrink-0"
                    style={{ backgroundColor: colors.bgColor, color: colors.textColor }}
                  >
                    {initials}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base font-semibold text-content-primary">
                      {patient.FirstName} {patient.LastName || ''}
                    </h4>
                    {patient.Phone && (
                      <p className="text-xs text-content-tertiary mt-0.5">{patient.Phone}</p>
                    )}
                  </div>
                  {/* Quick Book Button */}
                  <button
                    onClick={() => {
                      handleClose();
                      navigate(`${allRoutes.appointment}?book=${patient.UserId}`);
                    }}
                    className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-brand-50 text-brand-500 hover:bg-brand-100 transition-colors"
                    title="Book Appointment"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>

                {/* Quick Stats Bar */}
                <div className="flex gap-2">
                  {quickStats.map((stat) => (
                    <div key={stat.label} className={`flex-1 rounded-lg px-3 py-2 text-center ${stat.color || "bg-gray-50 text-content-secondary"}`}>
                      <p className="text-[10px] uppercase tracking-wide opacity-60 font-medium">{stat.label}</p>
                      <p className="text-xs font-semibold mt-0.5">{stat.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-surface-border px-5">
                {TABS.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-1 text-xs font-medium py-2.5 border-b-2 transition-colors ${
                      activeTab === tab
                        ? "border-brand-500 text-brand-500"
                        : "border-transparent text-content-tertiary hover:text-content-secondary"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="px-5 py-4">
                {/* ─── Overview Tab ─── */}
                {activeTab === "Overview" && (
                  <div>
                    {/* Info Grid */}
                    <div className="space-y-0 border border-gray-100 rounded-lg overflow-hidden">
                      {infoRows.map((row, i) =>
                        row.value ? (
                          <div
                            key={row.label}
                            className={`flex justify-between px-4 py-2.5 text-xs ${
                              i % 2 === 0 ? "bg-gray-50/50" : "bg-white"
                            }`}
                          >
                            <span className="text-content-tertiary font-medium">{row.label}</span>
                            <span className="text-content-primary text-right max-w-[60%]">{row.value}</span>
                          </div>
                        ) : null
                      )}
                    </div>

                    {/* Symptoms */}
                    {patient.symptomsDescription && (
                      <div className="mt-4">
                        <p className="text-[11px] text-content-tertiary font-medium uppercase tracking-wide mb-1.5">Symptoms</p>
                        <p className="text-xs text-content-secondary bg-gray-50 rounded-lg px-3 py-2.5 leading-relaxed">
                          {patient.symptomsDescription}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* ─── Sessions Tab ─── */}
                {activeTab === "Sessions" && (
                  <div>
                    {sessionsLoading ? (
                      <SkeletonRows count={5} />
                    ) : sessions.length > 0 ? (
                      <div className="space-y-2">
                        {sessions.map((s, i) => (
                          <div key={s.id || i} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                            {/* Status dot */}
                            <span className={`w-2 h-2 rounded-full shrink-0 ${
                              s.is_absent ? "bg-red-400" : s.is_complete ? "bg-green-400" : "bg-yellow-400"
                            }`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <p className="text-xs font-medium text-content-primary truncate">
                                  {s.session_name || s.session_type || "Session"}
                                </p>
                                {Number(s.google_meet_enabled) === 1 && (
                                  <span className="inline-flex shrink-0" title="Google Meet session" aria-label="Google Meet session" data-testid="drawer-session-google-meet">
                                    <GoogleMeetIcon className="w-3.5 h-3.5" />
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-content-tertiary">
                                {formatDate(s.date)} · {s.start_time ? formatTime(s.start_time) : ""}{s.end_time ? `–${formatTime(s.end_time)}` : ""}
                              </p>
                            </div>
                            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 ${
                              s.is_absent
                                ? "bg-red-50 text-red-600"
                                : s.is_complete
                                ? "bg-green-50 text-green-600"
                                : "bg-yellow-50 text-yellow-600"
                            }`}>
                              {s.is_absent ? "Absent" : s.is_complete ? "Done" : "Pending"}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <svg className="w-10 h-10 mx-auto text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-xs text-content-tertiary">No sessions this month</p>
                      </div>
                    )}
                  </div>
                )}

                {/* ─── Payments Tab ─── */}
                {activeTab === "Payments" && (
                  <div>
                    {paymentsLoading ? (
                      <SkeletonRows count={4} />
                    ) : payments.length > 0 ? (
                      <div>
                        {/* Payment Summary Bar */}
                        {(() => {
                          const totalAmount = payments.reduce((sum, p) => sum + (p.total_amount || 0), 0);
                          const paidAmount = payments.reduce((sum, p) => sum + (p.paid_amount || 0), 0);
                          const pendingAmount = totalAmount - paidAmount;
                          return (
                            <div className="flex gap-2 mb-3">
                              <div className="flex-1 bg-green-50 rounded-lg px-3 py-2 text-center">
                                <p className="text-[10px] text-green-600 uppercase tracking-wide font-medium">Paid</p>
                                <p className="text-xs font-bold text-green-700 mt-0.5">{currency}{paidAmount.toLocaleString("en-IN")}</p>
                              </div>
                              <div className="flex-1 bg-orange-50 rounded-lg px-3 py-2 text-center">
                                <p className="text-[10px] text-orange-600 uppercase tracking-wide font-medium">Pending</p>
                                <p className="text-xs font-bold text-orange-700 mt-0.5">{currency}{pendingAmount.toLocaleString("en-IN")}</p>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Payment List */}
                        <div className="space-y-2">
                          {payments.map((p, i) => (
                            <div key={p.invoice_id || i} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                              <span className={`w-2 h-2 rounded-full shrink-0 ${
                                p.is_full_amount_paid ? "bg-green-400" : "bg-orange-400"
                              }`} />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-content-primary truncate">
                                  {p.session_name || "Payment"} · {p.session_type || ""}
                                </p>
                                <p className="text-[11px] text-content-tertiary">{formatDate(p.date)}</p>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-xs font-semibold text-content-primary">{currency}{(p.total_amount || 0).toLocaleString("en-IN")}</p>
                                {!p.is_full_amount_paid && (
                                  <p className="text-[10px] text-orange-500">{currency}{(p.remaining_amount || 0).toLocaleString("en-IN")} due</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <svg className="w-10 h-10 mx-auto text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z" />
                        </svg>
                        <p className="text-xs text-content-tertiary">No payment records</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-xs text-content-tertiary text-center py-8">No patient data found</p>
          )}
        </div>

        {/* Footer */}
        {patient && (
          <div className="px-5 py-3 border-t border-surface-border flex-shrink-0 flex gap-2">
            <button
              onClick={() => {
                handleClose();
                navigate(`${allRoutes.patientprofile}?UserId=${patient.UserId}`);
              }}
              className="flex-1 text-xs font-medium text-brand-500 border border-brand-500 rounded-md py-2 hover:bg-brand-500 hover:text-white transition-all"
            >
              View Full Profile
            </button>
            <button
              onClick={() => {
                handleClose();
                navigate(`${allRoutes.addpatient}?UserId=${patient.UserId}`);
              }}
              className="flex-1 text-xs font-medium text-white bg-brand-500 rounded-md py-2 hover:bg-brand-600 transition-all"
            >
              Edit Profile
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default PatientDetailSlidePanel;
